// Orchestrator for custom-asset deletion. Mirrors vanilla
// `deleteCustomAsset` (legacy/index.html:3709-3814) one-to-one:
//
//   1. POST /delete-asset for the target asset's WebDAV folder + KV record.
//   2. For every asset that comes AFTER the deleted one in the combined list,
//      POST /rename-asset to renumber its folder (the folder name is built
//      from the asset's positional index, so a delete shifts every following
//      folder name down by one).
//   3. On success, mutate the local Zustand store: drop the custom asset, its
//      versions entry, and any per-asset blocks/free-mode caches.
//
// Vanilla iteration order (legacy/index.html:3752):
//   `for (let i = combinedIdx + 1; i < allBefore.length; i++)`
// i.e. forward from the deletion point to the end. That order is safe because
// every rename SHRINKS the index by 1 (e.g. folder "5 - Custom - X" becomes
// "4 - Custom - X"); the new name never collides with any folder we have not
// yet touched, so no temp-name juggling is needed. We mirror it verbatim.
//
// Conservative on failure (per audit item #7): if any HTTP call rejects, we
// surface a toast and bail BEFORE touching local state. The user can retry;
// half-renumbered remote vs. local state is the worst outcome.
//
// Caller already wraps this in `setAssetBusy(idx, true)` (item #6 →
// `Generation/index.tsx:handleDeleteCustomAsset`), so we do not double-wrap.

import {
  deleteCustomAsset as apiDeleteCustomAsset,
  renameAsset as apiRenameAsset,
} from '@/lib/api';
import { buildAssetFolderName } from '@/lib/uploadFilenames';
import { selectAllAssets, useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { shiftIndexMapAfterDelete } from '@/lib/reindexMap';

export interface DeleteCustomAssetResult {
  ok: boolean;
  /** Human-readable error suitable for a toast on failure. `null` on success. */
  error: string | null;
}

/**
 * Run the full delete + renumber + local-cleanup flow for a custom asset.
 *
 * @param assetId — id of the custom asset to delete (matches `Asset.id`).
 * @param opts.skipNetwork — when true, skip both API calls and only update
 *   local state. Two callers:
 *   1. asset has no uploaded versions — no remote folder exists yet, so
 *      nothing to delete or rename (mirrors vanilla's `if (hasVersions)`
 *      gate at legacy:3734).
 *   2. asset HAS uploaded versions but the user explicitly opted OUT of
 *      cloud cleanup via the DeleteAssetModal checkbox (2026-04-29). The
 *      cloud folder is left intact so the client/admin can keep accessing
 *      past uploads — see TRADE-OFF below.
 *
 * TRADE-OFF (skipNetwork=true with versions present):
 *   `applyLocalDelete` ALWAYS reindexes per-asset maps (assetBlocks,
 *   stsAssets, freeBlocks, etc.) so the local indices stay packed. The
 *   alternative — preserving local index gaps to keep them aligned with
 *   the cloud folder names — would diverge from `selectAllAssets`'s
 *   "predefined ++ customAssets" indexing scheme and require pervasive
 *   gap-handling everywhere. Instead we accept that the cloud folder for
 *   the deleted asset becomes orphaned: it stays put with its versions,
 *   and any trailing custom assets (now at lower local indices) will
 *   upload to NEW folder names on next upload, creating fresh folders
 *   while leaving the old one intact-but-orphaned. This is the documented
 *   semantic of the opt-out path: "I want to clear my session of this
 *   asset, but I want my client to keep accessing past uploads."
 */
export async function deleteCustomAssetWithCleanup(
  assetId: string,
  opts: { skipNetwork?: boolean } = {},
): Promise<DeleteCustomAssetResult> {
  const store = useSessionStore.getState();
  const { toolId, sessionFolder } = store;
  const assets = selectAllAssets(store);
  const combinedIdx = assets.findIndex((a) => a.id === assetId);
  if (combinedIdx < 0) {
    return { ok: false, error: 'Asset not found' };
  }
  const target = assets[combinedIdx];

  // Vanilla mirrors `combinedIdx = (CONFIG.assets || []).length + customIdx`
  // (legacy/index.html:3713). Same value via `findIndex` since custom assets
  // always sort after predefined ones in `selectAllAssets`.
  const targetFolder = buildAssetFolderName({
    assetIdx: combinedIdx,
    title: target.title,
    isCustom: true,
  });

  // Pre-compute the rename plan from CURRENT state — so a failure on the
  // delete-asset POST leaves the local store untouched (we don't mutate
  // until everything succeeds). Mirrors vanilla's `allBefore = getAllAssets()`
  // snapshot at legacy:3751.
  const renamePlan: { assetId: string; oldName: string; newName: string }[] = [];
  for (let i = combinedIdx + 1; i < assets.length; i++) {
    const a = assets[i];
    const isCustom = i >= (store.config?.assets.length ?? 0);
    const oldName = buildAssetFolderName({
      assetIdx: i,
      title: a.title,
      isCustom,
    });
    // After deletion, this asset shifts to (i - 1). Mirrors vanilla's
    // `const newNum = i;` at legacy:3755 (1-based via `+1` in builder, so
    // newAssetIdx in 0-based terms is `i - 1`).
    const newName = buildAssetFolderName({
      assetIdx: i - 1,
      title: a.title,
      isCustom,
    });
    if (oldName !== newName) renamePlan.push({ assetId: a.id, oldName, newName });
  }

  if (!opts.skipNetwork) {
    if (!toolId || !sessionFolder) {
      return { ok: false, error: 'Session not initialised' };
    }
    // 1) Delete the target folder + clear KV versions for the asset.
    try {
      await apiDeleteCustomAsset(toolId, {
        sessionFolder,
        assetIdx: combinedIdx,
        assetId,
        assetFolder: targetFolder,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return { ok: false, error: `Delete failed: ${msg}` };
    }
    // 2) Renumber every following asset folder. Forward order — see header
    //    comment for why no collisions are possible.
    for (const step of renamePlan) {
      try {
        await apiRenameAsset(toolId, sessionFolder, step.oldName, step.newName, step.assetId);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        return {
          ok: false,
          error: `Rename failed (${step.oldName} to ${step.newName}): ${msg}`,
        };
      }
    }
  }

  // 3) Local store cleanup — only after every network call succeeds.
  //    Mirrors legacy/index.html:3777-3799.
  applyLocalDelete(assetId, combinedIdx);
  return { ok: true, error: null };
}

/** Splice the custom asset out, drop its versions slot, and reindex any
 *  per-combined-index maps (`assetBlocks`, `freeBlocks`, `freeMode`,
 *  `freeActiveIdx`) so keys above `combinedIdx` shift down by 1. Mirrors
 *  vanilla's reindex helper at legacy/index.html:3786-3799.
 *
 *  Runs unconditionally — even when the caller passed `skipNetwork: true`.
 *  See the orchestrator's TRADE-OFF comment for why we keep local indices
 *  packed at the cost of orphaning the cloud folder in the opt-out path. */
function applyLocalDelete(assetId: string, combinedIdx: number): void {
  useSessionStore.setState((s) => {
    const reindex = <V,>(obj: Record<number, V>): Record<number, V> =>
      shiftIndexMapAfterDelete(obj, combinedIdx);
    const nextVersions = { ...s.versions };
    delete nextVersions[assetId];
    // Clamp currentAssetIdx so it never points past the new last asset.
    // Mirrors legacy/index.html:3804-3809.
    const predefinedCount = s.config?.assets.length ?? 0;
    const nextCustomCount = s.customAssets.filter((a) => a.id !== assetId).length;
    const total = predefinedCount + nextCustomCount;
    let nextCurrent = s.currentAssetIdx;
    if (total === 0) nextCurrent = 0;
    else if (nextCurrent >= total) nextCurrent = total - 1;
    return {
      customAssets: s.customAssets.filter((a) => a.id !== assetId),
      assetBlocks: reindex(s.assetBlocks),
      stsAssets: reindex(s.stsAssets),
      customDurations: reindex(s.customDurations),
      customVideoEnabled: reindex(s.customVideoEnabled),
      freeMode: reindex(s.freeMode),
      freeBlocks: reindex(s.freeBlocks),
      freeActiveIdx: reindex(s.freeActiveIdx),
      // sfxBeds is combined-index-keyed like the maps above but was missed
      // here, so a deleted custom asset left its bed on the asset that moved
      // into the freed slot — that bed then rendered into the wrong upload.
      sfxBeds: reindex(s.sfxBeds),
      versions: nextVersions,
      currentAssetIdx: nextCurrent,
    };
  });
  // The video store is a separate zustand store, also combined-index-keyed —
  // reindex it in lockstep so a trailing asset's reference video follows it
  // instead of being inherited by the shifted-down asset (and the deleted
  // asset's own blob source gets revoked).
  useVideoStore.getState().reindexAfterDelete(combinedIdx);
}
